import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import {
  TP_DETAIL_KEY,
  TP_LIST_KEY,
  getIntegrationDetail,
  putIntegration,
  testIntegration,
} from "@/api/third-party-api";

const WASABI_KEY = "wasabi";

/**
 * Admin → Storage.
 *
 * Where case images, DICOM files and candidate recordings are written.
 *
 * **THE SWITCH IS NOT ON THIS SCREEN, AND THAT IS DELIBERATE.** Whether
 * the system uses object storage at all is `MEDIA_BACKEND` in the
 * server's own `deploy/.env`. The clinic system chose its storage
 * backend from a database row, which is why object storage was deleted
 * on extraction rather than carried across dormant: a database restored
 * from somewhere else could silently start shipping candidate case
 * images to a third party. Keeping the switch off the database is what
 * answers that. These five fields are the bucket's credentials; they
 * cannot turn anything on by themselves.
 *
 * Everything else here follows MailPanel, including the reason: only
 * fields the admin actually TYPED are sent, because seeding the form
 * from `config_masked` and posting it wholesale writes bullets over the
 * real secret key and destroys it with no error.
 */
export function StoragePanel() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: TP_DETAIL_KEY(WASABI_KEY),
    queryFn: () => getIntegrationDetail(WASABI_KEY),
  });

  const [edited, setEdited] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState<boolean | null>(null);

  useEffect(() => {
    if (enabled === null && detail.data) setEnabled(detail.data.enabled);
  }, [detail.data, enabled]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: TP_DETAIL_KEY(WASABI_KEY) });
    void qc.invalidateQueries({ queryKey: TP_LIST_KEY });
  };

  const failure = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback;

  const save = useMutation({
    mutationFn: () => {
      const cfg: Record<string, unknown> = {
        ...(detail.data?.config_raw ?? {}),
      };
      for (const f of detail.data?.schema.fields ?? []) {
        const typed = edited[f.name];
        if (typed === undefined) continue;
        cfg[f.name] = f.type === "boolean" ? typed === "true" : typed;
      }
      return putIntegration(WASABI_KEY, {
        config: cfg,
        enabled: enabled ?? false,
      });
    },
    onSuccess: () => {
      setEdited({});
      refresh();
      toast({
        title: "Storage settings saved",
        description: "They take effect the next time the backend restarts.",
      });
    },
    onError: (err) =>
      toast({ title: failure(err, "Could not save"), variant: "error" }),
  });

  // The test runs against what is STORED, not what is typed — the same
  // rule as Mail. Testing the form's contents would pass on credentials
  // that were never written, which is the one result nobody should be
  // given.
  const test = useMutation({
    mutationFn: () => testIntegration(WASABI_KEY, null),
    onSuccess: (r) => {
      refresh();
      toast({
        title: r.message,
        ...(r.details ? { description: r.details } : {}),
        variant: r.success ? "success" : "error",
      });
    },
    onError: (err) =>
      toast({ title: failure(err, "Could not test"), variant: "error" }),
  });

  const schema = detail.data?.schema;
  const dirty = Object.keys(edited).length > 0;
  // From the ROW, not from `source`. The resolver behind `source` is
  // cached for 60 seconds per worker, and with four workers the read
  // after a save usually lands on one that has not seen it — so this
  // banner said "no bucket is configured" immediately after a
  // successful save, which reads as the save having failed.
  // `config_raw` comes straight off the row in the same response.
  const configured = Object.values(detail.data?.config_raw ?? {}).some(
    (v) => typeof v === "string" && v.trim() !== "",
  );
  const passed = detail.data?.last_test_succeeded === true;

  const shown = (name: string): string => {
    const typed = edited[name];
    if (typed !== undefined) return typed;
    const v = detail.data?.config_masked[name];
    return typeof v === "string" ? v : "";
  };

  return (
    <div data-cy="storage-panel" className="flex flex-col gap-6">
      {/* Whether the bucket is actually reachable, said first — the
          fields below are configuration, this is its consequence. */}
      <section
        data-cy="storage-status"
        className={[
          "flex max-w-2xl items-start gap-3 rounded-xl border px-4 py-3",
          passed
            ? "border-status-done/40 bg-status-done-bg"
            : "border-border bg-background",
        ].join(" ")}
      >
        {passed ? (
          <CheckCircle2
            aria-hidden
            className="mt-0.5 h-5 w-5 text-status-done"
          />
        ) : (
          <XCircle
            aria-hidden
            className="mt-0.5 h-5 w-5 text-foreground-muted"
          />
        )}
        <div className="text-sm">
          <p className="font-semibold text-foreground">
            {passed
              ? "The bucket is reachable"
              : configured
                ? "The bucket has not tested successfully"
                : "No bucket is configured"}
          </p>
          <p className="mt-0.5 text-foreground-muted">
            {passed
              ? "New files are written here once the server is set to use object storage."
              : configured
                ? (detail.data?.last_test_error ??
                  "Press Test connection to check these details.")
                : "Files are being written to the server's own disk."}
          </p>
        </div>
      </section>

      <section className="max-w-2xl rounded-xl border border-border bg-background-card p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {schema?.display_name ?? "Object storage"}
        </h3>
        <p className="mt-1 text-xs text-foreground-muted">
          {schema?.description ?? "The bucket this system writes files to."}
        </p>

        {detail.isLoading ? (
          <p className="mt-4 text-sm text-foreground-muted">Loading…</p>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {schema?.fields.map((f) => (
              <label
                key={f.name}
                className="flex flex-col text-xs text-foreground-muted"
              >
                {f.label}
                <input
                  type="text"
                  data-cy={`storage-field-${f.name}`}
                  autoComplete="off"
                  spellCheck={false}
                  value={shown(f.name)}
                  placeholder={f.placeholder}
                  onFocus={() => {
                    // A masked secret is not editable text. Clear it the
                    // moment the admin means to type, so nobody ends up
                    // editing bullets into a key.
                    if (f.secret && !(f.name in edited)) {
                      setEdited((v) => ({ ...v, [f.name]: "" }));
                    }
                  }}
                  onChange={(e) =>
                    setEdited((v) => ({ ...v, [f.name]: e.target.value }))
                  }
                  className="sk-input mt-1 w-auto font-mono"
                />
                {f.help_text && (
                  <span className="mt-1 text-[11px] text-foreground-muted">
                    {f.help_text}
                  </span>
                )}
              </label>
            ))}

            <label className="mt-1 flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                data-cy="storage-enabled"
                checked={enabled ?? false}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span>
                Use these credentials
                <span className="block text-[11px] text-foreground-muted">
                  Off leaves them stored but ignored, so a bucket can be
                  prepared before anything is written to it.
                </span>
              </span>
            </label>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-cy="btn-save-storage"
                disabled={save.isPending}
                onClick={() => save.mutate()}
                className="sk-btn sk-btn-primary"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                data-cy="btn-test-storage"
                disabled={test.isPending || dirty}
                title={
                  dirty
                    ? "Save first — the test uses the stored settings"
                    : "Reach the bucket with these credentials"
                }
                onClick={() => test.mutate()}
                className="sk-btn"
              >
                {test.isPending ? "Testing…" : "Test connection"}
              </button>
              {detail.data?.last_tested_at && !dirty && (
                <span className="text-xs text-foreground-muted">
                  {detail.data.last_test_succeeded
                    ? "Last test passed"
                    : `Last test failed — ${detail.data.last_test_error ?? "unknown"}`}
                </span>
              )}
            </div>
            <p className="text-[11px] text-foreground-muted">
              The test asks the bucket whether these credentials can reach
              it. Nothing is uploaded, so it can be run as often as you
              like.
            </p>
          </div>
        )}
      </section>

      {/* The two things an admin will otherwise be surprised by, said
          before they are. Both are consequences of the design rather
          than limitations worth hiding. */}
      <section className="max-w-2xl rounded-xl border border-border bg-background-card p-5">
        <h3 className="text-sm font-semibold text-foreground">
          What this screen does not do
        </h3>
        <ul className="mt-2 flex list-disc flex-col gap-2 pl-5 text-xs text-foreground-muted">
          <li>
            <span className="font-semibold text-foreground">
              It does not switch object storage on.
            </span>{" "}
            That is a server setting (<code>MEDIA_BACKEND</code> in{" "}
            <code>deploy/.env</code>), kept out of the database on purpose:
            a database restored from elsewhere must not be able to start
            sending this system&rsquo;s files somewhere new. Saving
            credentials here is safe on its own.
          </li>
          <li>
            <span className="font-semibold text-foreground">
              It does not move anything.
            </span>{" "}
            Every file records where it was written, so images and
            recordings already on the server keep being served from the
            server — indefinitely, and whichever way this is set. Only
            new uploads go to the bucket.
          </li>
          <li>
            Changes take effect when the backend restarts. Until then it
            keeps writing wherever it decided at startup.
          </li>
        </ul>
      </section>
    </div>
  );
}
